import * as React from 'react';
import { TranslateFn, AnsViewComp } from '@wix/answers-app-core';
import { Icon, FileSelector, DeprecatedTextAreaAutosize, CheckIcon, sendChat, plusChat, chatEmoji, close } from '@wix/answers-ui-libby';
import { EmojiPicker } from './emoji-picker';
import { emojiFromUnicode } from './emoji-picker/emojis';
import { getContentType, AttachmentTypes, getShortenedNumber, ErrorType } from '../utils';
import { classNames } from '@wix/answers-lib';

export const chatInputKey = 'chat-input';

export type SelectFileSuccess = void;
export type SelectFileError = string;

export type ChatInputProps = {
	value: string;
	onChange: (value: string) => void;
	onSend: () => void;
	uploadLimitInBytes: number;
	supportedFileTypes: string[];
	onSelectFile: (file: File, cb: (percent: number) => void) => Promise<void>;
	onCancel: () => void;
	placeholder: string;
	disabled?: boolean;
};

export type ChatInputState = {
	emojiOpen: boolean;
	errorType: string;
	progressCount: number;
	progressName: string;
	file?: File;
};

const translationPrefix = `widget-ng.chat.chat-room.${chatInputKey}`;

export class ChatInput extends AnsViewComp<ChatInputProps, ChatInputState> {

	state: ChatInputState = {
		emojiOpen: false,
		errorType: ErrorType.NONE,
		progressCount: 0,
		progressName: '',
	};

	tp: TranslateFn = (key, params) => {
		return this.props.t(`${translationPrefix}.${key}`, params);
	}

	onSend = () => {
		this.props.onSend();
		this.setState({emojiOpen: false, errorType: ErrorType.NONE});
	}

	handleEnterPress = (e: KeyboardEvent) => {
		if (!e.shiftKey) {
			if (e.keyCode === 13) {
				e.preventDefault();
				this.onSend();
			}
		}
	}

	componentDidMount () {
		if (window) {
			window.addEventListener('keypress', this.handleEnterPress);
		}
	}

	componentWillUnmount () {
		if (window) {
			window.removeEventListener('keypress', this.handleEnterPress);
		}
	}

	onEmojiOpen = () => {
		this.setState({emojiOpen: !this.state.emojiOpen});
	}

	onSelectEmoji = (emoji: string) => {
		const value = this.props.value + emojiFromUnicode(emoji);
		this.props.onChange(value);
	}

	onUploadProgress = (progressPercent: number) => {
		this.setState({progressCount: progressPercent});
	}

	onCancel = () => {
		this.props.onCancel();
		this.resetErrorAndProgress();

	}

	renderProgressBar = () => {
		const fullPercent = this.state.progressCount === 100;
		const showSuccess = fullPercent;
		const successMessage = this.tp('upload.success');
		const name = this.state.progressName;
		const count = this.state.progressCount;
		const uploadMessage = (
			<div className='upload-name-wrapper'>
				<div className='upload-name'>{this.tp('upload-name', { name })}</div>
				{`${count}%`}
			</div>
		);
		const done = <div className={'done'}> {<CheckIcon/>} </div>;
		const cancel = <div className='cancel' onClick={this.onCancel}> {this.tp('upload.cancel')} </div>;

		const progressBar = (
			<div className='progress-bar'>
			<div className='progress-percent' style={{width: `${this.state.progressCount}%`}}/>
				<div className='progress-details'>
					{showSuccess ? successMessage : uploadMessage}
				</div>
				<div className='progress-activation'>
					{showSuccess ? done : cancel}
				</div>
			</div>
		);
		return progressBar;
	}

	onSelectFile = (file: File) => {
		const isFileValid = this.isFileValid(file);
		if (isFileValid) {
			this.resetError();
			this.setState({progressName: file.name, progressCount: 1, file}, () =>
				this.props.onSelectFile(file, this.onUploadProgress)
				.then(() => {
					this.resetProgress();
				})
				.catch(() => {
					this.setState({errorType: ErrorType.UNKNOWN});
					this.resetProgress();
				})
			);
		}
	}

	isFileValid = (file: File) => {
		const maxSize = this.props.uploadLimitInBytes;
		const fileType = getContentType(file.type);
		const fileSize = file.size;
		if (fileSize > maxSize) {
			this.setState({errorType: ErrorType.SIZE});
			return false;
		} else if (fileType === AttachmentTypes.UNKNOWN) {
			this.setState({errorType: ErrorType.TYPE});
			return false;
		}
		return true;
	}

	resetProgress = () => this.setState({ progressCount: 0, progressName: '' });

	resetError = () => this.setState({errorType: ErrorType.NONE});

	resetErrorAndProgress = () => {
		this.resetError();
		this.resetProgress();
	}

	getErrorTranslation = (error: string) => {
		switch (error) {
			case ErrorType.SIZE:
				return 'error.size';
			case ErrorType.TYPE:
				return 'error.type';
			case ErrorType.UNKNOWN:
				return 'error.unknown';
			case ErrorType.NONE:
				return 'error.none';
			default:
				return 'error.none';
		}
	}

	onRetryFileUpload = () => {
		const file = this.state.file;
		if (file) {
			this.onSelectFile(file);
		}
	}

	renderErrorMessage = () => {
		const error = this.state.errorType;
		const errorTranslation = this.getErrorTranslation(this.state.errorType);
		const size =  getShortenedNumber(this.props.uploadLimitInBytes);
		const isSizeError = error === ErrorType.SIZE;
		const errorContent = (
			<span className='error-type'>
				{isSizeError ? this.tp(errorTranslation, {size}) : this.tp(errorTranslation)}
			</span>
		);
		const unknownErrorHelp = (
			<div className='error-help' onClick={this.onRetryFileUpload}> {this.tp('error.try-again')} </div>
		);
		const knownErrorHelp = (
			<div className='error-help'> {this.tp('error.choose-new')} </div>
		);

		const fileError = (
			<FileSelector onFileSelect={this.onSelectFile} supportedFileTypes={this.props.supportedFileTypes}>
				{knownErrorHelp}
			</FileSelector>
		);
		const errorMessage = (
			<div className='error'>
				<div className='error-message'>
					{errorContent}
					{error === ErrorType.UNKNOWN ? unknownErrorHelp : fileError}
				</div>
				<div className='close-error-message' onClick={this.resetErrorAndProgress}>
					<Icon className={'error-close'} icon={close} />
				</div>
			</div>
		);
		return errorMessage;
	}

	render () {
		const {props} = this;
		const send = (
			<div className='send-button' onClick={this.onSend}>
				<Icon icon={sendChat}/>
			</div>
		);
		const attach = (
			<FileSelector onFileSelect={this.onSelectFile} supportedFileTypes={this.props.supportedFileTypes}>
				<div className='attach-button'>
					<Icon icon={plusChat}/>
				</div>
			</FileSelector>
		);
		const hasValue = props.value !== '';

		const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

		const emojiButton = (
			isMac ? (
				<div className='emoji-button' onClick={this.onEmojiOpen}>
					<Icon icon={chatEmoji}/>
				</div>
			) : null
		);

		return (
			<div className={classNames(chatInputKey, {disabled: props.disabled})}>
				{this.state.progressCount !== 0 && this.state.errorType === ErrorType.NONE ? this.renderProgressBar() : null}
				{this.state.errorType !== ErrorType.NONE ? this.renderErrorMessage() : null}
				<div className='chat-input-field'>
					<DeprecatedTextAreaAutosize
						value={props.value}
						placeholder={this.props.placeholder}
						onChange={props.onChange}
						maxRows={4}
						disabled={props.disabled}
					/>
					<div className='chat-actions-wrapper'>
						{emojiButton}
						{hasValue ? send : attach}
					</div>
					{this.state.emojiOpen ? <EmojiPicker onSelectEmoji={this.onSelectEmoji}/> : null}
				</div>
			</div>
		);
	}
}
